/**
 * useActiveSeason – resolves the effective season for public place pages.
 *
 * Priority:
 * 1. URL query param ?season=winter|spring|summer|autumn (explicit override)
 * 2. Calendar season (Europe/Stockholm) – if an overlay exists for it
 * 3. Fallback: pick overlay with most recent updated_at
 *
 * Returns { activeSeason, source } where source helps with debugging.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCurrentSeason, parseSeasonParam, type Season } from '@/lib/season';
import { useSeasonOverlays } from '@/hooks/useSeasonOverlays';
import { EXPLORE_DEBUG } from '@/lib/debug';

type SeasonSource = 'url_param' | 'calendar' | 'fallback';

interface ActiveSeasonResult {
  activeSeason: Season;
  source: SeasonSource;
  isLoading: boolean;
}



export function useActiveSeason(
  placeId: string | null | undefined,
): ActiveSeasonResult {
  const [searchParams] = useSearchParams();

  // 1. URL param override
  const paramSeason = useMemo(
    () => parseSeasonParam(searchParams.get('season')),
    [searchParams],
  );

  // 2. Calendar season
  const calendarSeason = useMemo(() => getCurrentSeason(), []);

  // Fetch all overlays for the place to enable fallback logic
  const { data: allOverlays, isLoading } = useSeasonOverlays(placeId);

  const result = useMemo((): Omit<ActiveSeasonResult, 'isLoading'> => {
    // Priority 1: explicit URL param
    if (paramSeason) {
      if (EXPLORE_DEBUG) console.debug('[useActiveSeason]', { activeSeason: paramSeason, source: 'url_param' });
      return { activeSeason: paramSeason, source: 'url_param' };
    }

    // Build list of overlays that have actual content, sorted by updated_at DESC
    const overlaysWithContent = (allOverlays ?? [])
      .filter((o) =>
        o.summary_sv || o.hero_media_asset_id ||
        (o.gallery_media_asset_ids?.length ?? 0) > 0 ||
        o.did_you_know_sv || o.typical_duration,
      )
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));

    const availableSeasons = new Set<Season>(overlaysWithContent.map((o) => o.season as Season));

    // Priority 2: calendar season has an overlay
    if (availableSeasons.has(calendarSeason)) {
      if (EXPLORE_DEBUG) console.debug('[useActiveSeason]', { activeSeason: calendarSeason, source: 'calendar' });
      return { activeSeason: calendarSeason, source: 'calendar' };
    }

    // Priority 3: fallback – pick overlay with most recent updated_at
    if (overlaysWithContent.length > 0) {
      const fallback = overlaysWithContent[0].season as Season;
      if (EXPLORE_DEBUG) console.debug('[useActiveSeason]', { activeSeason: fallback, source: 'fallback', baseSeason: calendarSeason, available: [...availableSeasons] });
      return { activeSeason: fallback, source: 'fallback' };
    }

    // No overlays at all – use calendar season (no overlay will be applied)
    return { activeSeason: calendarSeason, source: 'calendar' };
  }, [paramSeason, calendarSeason, allOverlays]);

  return { ...result, isLoading };
}
